import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { AuthState, User, Company, LoginCredentials } from "@/types/auth";
import { authAPI } from "@/services/api";

const initialState: AuthState = {
  user: null,
  company: null,
  token: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
};

// Definir los tipos para los payloads de éxito
interface LoginSuccessPayload {
  user: User;
  company: Company;
  token: string;
  refreshToken: string;
}

interface InitializeSuccessPayload {
  user: User;
  company: Company;
  token: string;
  refreshToken: string | null;
}

interface RefreshTokenSuccessPayload {
  token: string;
  refreshToken: string;
}

export const loginUser = createAsyncThunk<
  LoginSuccessPayload,
  LoginCredentials,
  { rejectValue: string }
>("auth/login", async (credentials: LoginCredentials, { rejectWithValue }) => {
  try {
    const response = await authAPI.login(credentials);

    if (!response || !response.data) {
      throw new Error("Respuesta inválida del servidor");
    }

    const { user, company, tokens } = response.data;

    // Asegurar que los permisos sean un objeto
    let permissionsObject: Record<string, boolean | number> = {};

    if (Array.isArray(user.role.permissions)) {
      // Convertir array a objeto
      user.role.permissions.forEach((permission: string) => {
        permissionsObject[permission] = true;
      });
    } else if (typeof user.role.permissions === "object") {
      // Ya viene como objeto
      permissionsObject = user.role.permissions;
    }

    const adaptedCompany: Company = {
      ...company,
    };

    // Guardar tokens según rememberMe
    if (credentials.rememberMe) {
      localStorage.setItem("token", tokens.access_token);
      localStorage.setItem("refresh_token", tokens.refresh_token);
      localStorage.setItem(
        "user",
        JSON.stringify({
          ...user,
          role: {
            ...user.role,
            permissions: permissionsObject,
          },
        })
      );
      localStorage.setItem("company", JSON.stringify(adaptedCompany as Company));
    } else {
      sessionStorage.setItem("token", tokens.access_token);
      sessionStorage.setItem("refresh_token", tokens.refresh_token);
      sessionStorage.setItem(
        "user",
        JSON.stringify({
          ...user,
          role: {
            ...user.role,
            permissions: permissionsObject,
          },
        })
      );
      sessionStorage.setItem("company", JSON.stringify(adaptedCompany as Company));
    }

    // Adaptar la estructura del usuario para el estado
    const adaptedUser: User = {
      id: user.id,
      email: user.email,
      name: `${user.first_name} ${user.last_name}`,
      first_name: user.first_name,
      last_name: user.last_name,
      phone: user.phone,
      avatar_url: user.avatar_url,
      is_owner: user.is_owner,
      role: {
        id: user.role.id,
        name: user.role.name,
        permissions: permissionsObject, // Usar el objeto convertido
      },
    };

    return {
      user: adaptedUser,
      company: adaptedCompany as Company,
      token: tokens.access_token,
      refreshToken: tokens.refresh_token,
    };
  } catch (error: unknown) {
    // Limpiar tokens en caso de error
    localStorage.removeItem("token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("user");
    localStorage.removeItem("company");
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("refresh_token");
    sessionStorage.removeItem("user");
    sessionStorage.removeItem("company");

    // Determinar mensaje de error
    let message = "Error de conexión con el servidor";

    if (typeof error === "object" && error !== null && "response" in error) {
      const axiosError = error as {
        response?: { data?: { body?: { message?: string }; message?: string }; status?: number };
      };
      // Error de respuesta del servidor
      message =
        axiosError.response?.data?.body?.message ||
        axiosError.response?.data?.message ||
        `Error del servidor: ${axiosError.response?.status}`;
    } else if (typeof error === "object" && error !== null && "request" in error) {
      // Error de red (sin respuesta)
      message =
        "No se pudo conectar con el servidor. Verifica tu conexión a internet.";
    } else if (error instanceof Error) {
      // Error de configuración de la solicitud
      message = error.message;
    }

    return rejectWithValue(message);
  }
});

export const getCurrentProfile = createAsyncThunk<
  { user: User; company: Company },
  void,
  { rejectValue: string }
>("auth/getProfile", async (_, { rejectWithValue }) => {
  try {
    const response = await authAPI.getProfile();
    if (!response || !response.data) {
      throw new Error("Respuesta inválida del servidor");
    }

    const userData = response.data;
    const user = userData;
    const company = userData.company;

    // Asegurar que los permisos estén en formato objeto
    let permissionsObject: Record<string, boolean | number> = {};
    
    if (user.role && Array.isArray(user.role.permissions)) {
      // Convertir array a objeto
      user.role.permissions.forEach((permission: string) => {
        permissionsObject[permission] = true;
      });
    } else if (user.role && typeof user.role.permissions === "object") {
      permissionsObject = user.role.permissions;
    }

    const adaptedUser: User = {
      id: user.id,
      email: user.email,
      name: `${user.first_name} ${user.last_name}`,
      first_name: user.first_name,
      last_name: user.last_name,
      phone: user.phone,
      avatar_url: user.avatar_url,
      is_owner: user.is_owner,
      role: {
        id: user.role.id,
        name: user.role.name,
        permissions: permissionsObject,
      },
    };

    const adaptedCompany: Company = {
      ...company,
    };

    // Actualizar storage para persistencia
    localStorage.setItem("user", JSON.stringify(adaptedUser));
    localStorage.setItem("company", JSON.stringify(adaptedCompany as Company));

    return {
      user: adaptedUser,
      company: adaptedCompany as Company,
    };
  } catch (error: any) {
    return rejectWithValue(error.message || "Error al obtener perfil");
  }
});

export const initializeAuth = createAsyncThunk<
  InitializeSuccessPayload,
  void,
  { rejectValue: string }
>("auth/initialize", async (_, { rejectWithValue }) => {
  try {
    // Buscar token en ambos storages
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");
    const refreshToken =
      localStorage.getItem("refresh_token") ||
      sessionStorage.getItem("refresh_token");
    const userStr =
      localStorage.getItem("user") || sessionStorage.getItem("user");
    const companyStr =
      localStorage.getItem("company") || sessionStorage.getItem("company");

    if (!token || !userStr || !companyStr) {
      // Limpiar storages si falta algún dato
      localStorage.removeItem("token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("user");
      localStorage.removeItem("company");
      sessionStorage.removeItem("token");
      sessionStorage.removeItem("refresh_token");
      sessionStorage.removeItem("user");
      sessionStorage.removeItem("company");

      throw new Error("Sesión no válida. Por favor, inicia sesión nuevamente.");
    }

    // Parsear datos guardados
    let user: User = JSON.parse(userStr);
    const company: Company = JSON.parse(companyStr);

    // Asegurar que los permisos estén en formato objeto
    if (user.role && Array.isArray(user.role.permissions)) {
      // Convertir array a objeto
      const permissionsObject: Record<string, boolean | number> = {};
      user.role.permissions.forEach((permission: string) => {
        permissionsObject[permission] = true;
      });

      user = {
        ...user,
        role: {
          ...user.role,
          permissions: permissionsObject,
        },
      };
    }

    return {
      user,
      company,
      token,
      refreshToken,
    };
  } catch (error: unknown) {
    // Limpiar todo en caso de error
    localStorage.removeItem("token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("user");
    localStorage.removeItem("company");
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("refresh_token");
    sessionStorage.removeItem("user");
    sessionStorage.removeItem("company");

    return rejectWithValue(
      error instanceof Error ? error.message : "Error al inicializar la autenticación"
    );
  }
});

export const refreshAccessToken = createAsyncThunk<
  RefreshTokenSuccessPayload,
  void,
  { rejectValue: string }
>("auth/refreshToken", async (_, { rejectWithValue, getState }) => {
  try {
    const state = getState() as { auth: AuthState };
    const refreshToken =
      state.auth.refreshToken ||
      localStorage.getItem("refresh_token") ||
      sessionStorage.getItem("refresh_token");

    if (!refreshToken) {
      throw new Error("No hay refresh token disponible");
    }

    // Aquí llamarías a tu endpoint de refresh token
    // Por ahora devolvemos un error ya que no tengo el endpoint
    // const response = await authAPI.refreshToken(refreshToken);

    throw new Error("Endpoint de refresh token no implementado");

    // Si tuvieras el endpoint:
    // return {
    //   token: response.data.access_token,
    //   refreshToken: response.data.refresh_token || refreshToken
    // };
  } catch (error: unknown) {
    return rejectWithValue(
      error instanceof Error ? error.message : "Error al refrescar el token",
    );
  }
});

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state) => {
      // Limpiar estado
      state.user = null;
      state.company = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.error = null;

      // Limpiar storages
      localStorage.removeItem("token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("user");
      localStorage.removeItem("company");
      localStorage.removeItem("companyBranding");
      sessionStorage.removeItem("token");
      sessionStorage.removeItem("refresh_token");
      sessionStorage.removeItem("user");
      sessionStorage.removeItem("company");

      // Llamar API de logout si existe
      authAPI.logout();
    },
    clearAuth: (state) => {
      state.user = null;
      state.company = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.error = null;

      localStorage.removeItem("token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("user");
      localStorage.removeItem("company");
      localStorage.removeItem("companyBranding");
      sessionStorage.removeItem("token");
      sessionStorage.removeItem("refresh_token");
      sessionStorage.removeItem("user");
      sessionStorage.removeItem("company");
    },
    clearError: (state) => {
      state.error = null;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setCredentials: (
      state,
      action: PayloadAction<{
        user: User;
        token: string;
        refreshToken?: string;
        company?: Company;
      }>
    ) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.refreshToken = action.payload.refreshToken || state.refreshToken;
      state.company = action.payload.company || state.company;
      state.isAuthenticated = true;
      state.isLoading = false;
    },
    updateUser: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };

        // Actualizar en storage
        const storage = localStorage.getItem("token")
          ? localStorage
          : sessionStorage;
        storage.setItem("user", JSON.stringify(state.user));
      }
    },
    updateCompany: (state, action: PayloadAction<Partial<Company>>) => {
      if (state.company) {
        state.company = { ...state.company, ...action.payload };

        // Actualizar en storage
        const storage = localStorage.getItem("token")
          ? localStorage
          : sessionStorage;
        storage.setItem("company", JSON.stringify(state.company));
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        loginUser.fulfilled,
        (state, action: PayloadAction<LoginSuccessPayload>) => {
          state.isLoading = false;
          state.isAuthenticated = true;
          state.user = action.payload.user;
          state.company = action.payload.company;
          state.token = action.payload.token;
          state.refreshToken = action.payload.refreshToken;
          state.error = null;
        }
      )
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.company = null;
        state.token = null;
        state.refreshToken = null;
        state.error = action.payload as string;
      })
      .addCase(initializeAuth.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(
        initializeAuth.fulfilled,
        (state, action: PayloadAction<InitializeSuccessPayload>) => {
          state.isLoading = false;
          state.isAuthenticated = true;
          state.user = action.payload.user;
          state.company = action.payload.company;
          state.token = action.payload.token;
          state.refreshToken = action.payload.refreshToken;
          state.error = null;
        }
      )
      .addCase(initializeAuth.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.company = null;
        state.token = null;
        state.refreshToken = null;
        state.error = action.payload as string;
      })
      .addCase(getCurrentProfile.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(
        getCurrentProfile.fulfilled,
        (state, action: PayloadAction<{ user: User; company: Company }>) => {
          state.isLoading = false;
          state.user = action.payload.user;
          state.company = action.payload.company;
          state.isAuthenticated = true;
        }
      )
      .addCase(getCurrentProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(refreshAccessToken.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(
        refreshAccessToken.fulfilled,
        (state, action: PayloadAction<RefreshTokenSuccessPayload>) => {
          state.isLoading = false;
          state.token = action.payload.token;
          state.refreshToken = action.payload.refreshToken;
          state.error = null;

          // Actualizar en storage
          const storage = localStorage.getItem("token")
            ? localStorage
            : sessionStorage;
          storage.setItem("token", action.payload.token);
          if (action.payload.refreshToken) {
            storage.setItem("refresh_token", action.payload.refreshToken);
          }
        }
      )
      .addCase(refreshAccessToken.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;

        // Si falla el refresh, limpiar todo
        if (
          action.payload === "No hay refresh token disponible" ||
          action.payload === "Endpoint de refresh token no implementado"
        ) {
          // No hacemos logout automático, solo marcamos error
        }
      });
  },
});

export const {
  logout,
  clearAuth,
  clearError,
  setLoading,
  setCredentials,
  updateUser,
  updateCompany,
} = authSlice.actions;

export default authSlice.reducer;
